import React, { createContext, useContext, useEffect, useRef, ReactNode, useMemo } from 'react';
import { useAuth } from './AuthProvider';
import { useBrand } from './BrandContext';
import { useInitializeClientPreferences } from '../hooks/Preferences';
import type { Client } from '../types/preferences';

interface ClientPreferencesContextType {
  clientData: Client | null;
  isLoading: boolean;
  error: string | null;
  enseigneCode: string;
  clientId: string | null;
}

const ClientPreferencesContext = createContext<ClientPreferencesContextType | undefined>(undefined);

interface ClientPreferencesProviderProps {
  children: ReactNode;
}

export const ClientPreferencesProvider: React.FC<ClientPreferencesProviderProps> = ({ children }) => {
  const { isAuthenticated, keycloakInstance } = useAuth();
  const { brand } = useBrand();
  const { initializeClient, isInitializing, error, clientData } = useInitializeClientPreferences();

  const hasInitializedRef = useRef(false);
  const isInitializingRef = useRef(false);
  const lastInitTimestampRef = useRef<number>(0);

  const clientId = useMemo(() => {
    return keycloakInstance?.tokenParsed?.card_number ||
           keycloakInstance?.tokenParsed?.carte ||
           keycloakInstance?.tokenParsed?.barcode ||
           process.env.REACT_APP_DEFAULT_IDENTIFIER ||
           '4007007007007';
  }, [keycloakInstance?.tokenParsed]);

  const clientName = keycloakInstance?.tokenParsed?.name || 
                     keycloakInstance?.tokenParsed?.preferred_username || 
                     'Utilisateur';

  useEffect(() => {
    const initialize = async () => {
      const now = Date.now();
      const timeSinceLastInit = now - lastInitTimestampRef.current;
      
      if (hasInitializedRef.current || isInitializingRef.current || !isAuthenticated || !clientId || timeSinceLastInit < 1000) {
        return;
      }

      lastInitTimestampRef.current = now;
      isInitializingRef.current = true;

      try {
        await initializeClient(brand.code, brand.displayName, clientId, clientName);
        hasInitializedRef.current = true;
      } catch (err) {
        console.error('Erreur initialisation préférences:', err);
      } finally {
        isInitializingRef.current = false;
      }
    };

    initialize();
  }, [isAuthenticated, clientId, brand.code, brand.displayName, clientName, initializeClient]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasInitializedRef.current = false;
      lastInitTimestampRef.current = 0;
    }
  }, [isAuthenticated]);

  const contextValue: ClientPreferencesContextType = {
    clientData,
    isLoading: isInitializing,
    error,
    enseigneCode: brand.code,
    clientId,
  };

  return (
    <ClientPreferencesContext.Provider value={contextValue}>
      {children}
    </ClientPreferencesContext.Provider>
  );
};

export const useClientPreferencesContext = (): ClientPreferencesContextType => {
  const context = useContext(ClientPreferencesContext);
  if (!context) {
    throw new Error('useClientPreferencesContext must be used within a ClientPreferencesProvider');
  }
  return context;
};

export const useClientPreferencesData = () => useClientPreferencesContext();

